import Identities from "orbit-db-identity-provider"
import {useCallback, useContext} from "react"
import {BackendContext} from "../context/BackendContext"
import {AppConfig} from "../App.config"
import {Keys} from "../interfaces/keys"

export function useAPI() {
  const {setIsGitSupported} = useContext(BackendContext)

  const healthCheck = useCallback(async (): Promise<boolean> => {
    try {
      const resp = await fetch(AppConfig.localServerUrl)
      if (!resp.ok) {
        return false
      }
      const json: { msg: string } = await resp.json()
      return json.msg === "OK"
    } catch (e) {
      return false
    }
  }, [])

  const sync = useCallback(async (keys: Keys, address: string, userName: string): Promise<boolean> => {
    const webIdentity = await Identities.createIdentity({
      type: "Ed25519IdentityProvider",
      keypair: keys.webIpfs
    })
    const desktopIdentity = await Identities.createIdentity({
      type: "Ed25519IdentityProvider",
      keypair: keys.desktopIpfs
    })

    const webIdentityId = webIdentity.id
    const desktopIdentityId = desktopIdentity.id
    const desktopPrivateKey = keys.desktopIpfs.privateKey.toString("hex")

    try {
      const resp = await fetch(
        AppConfig.localServerUrl + "/sync",
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            "web-identity-id": webIdentityId,
            "desktop-identity-id": desktopIdentityId,
            "desktop-private-key": desktopPrivateKey,
            "profile-address": address,
            "user-name": userName
          }
        }
      )
      if (resp.ok) {
        // On successful handshake with local git server, this web client stores some metadata of the server
        const {metadata} = await resp.json()
        setIsGitSupported && setIsGitSupported(metadata.isSupportedGit)
      }
      return resp.ok
    } catch (e) {
      return false
    }
  }, [])

  const createRepo = useCallback(async (
    repoName: string,
    repoDesc: string,
    accessController: { write: string[] }
  ): Promise<{ success: boolean, msg?: string, ipfsCID?: string, repoId?: string }> => {
    const url = `${AppConfig.localServerUrl}/action/create-repo`
    const resp = await fetch(
      url,
      {
        method: "POST",
        body: JSON.stringify({
          name: repoName,
          desc: repoDesc,
          accessController
        }),
        headers: {
          "content-type": "application/json"
        }
      }
    )
    const json = await resp.json()
    return {success: resp.ok, ...json}
  }, [])

  const mergeBranches = async (
    repoAddr: string,
    baseBranch: string,
    mergeBranch: string
  ) => {
    const url = `${AppConfig.localServerUrl}/action/merge-branches`
    const resp = await fetch(
      url,
      {
        method: "POST",
        body: JSON.stringify({
          repoAddr,
          baseBranch,
          mergeBranch,
        }),
        headers: {
          "content-type": "application/json"
        }
      }
    )
    const json = await resp.json()
    return {success: resp.ok, ...json}
  }

  return {
    healthCheck,
    sync,
    createRepo,
    mergeBranches: useCallback(mergeBranches, [])
  }
}
